import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { adminApi } from '@/utils/api'
import type { AdminOrganizationStat } from '@/types/admin'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

export default function AdminOrganizationsPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [pending, setPending] = useState<AdminOrganizationStat[]>([])
  const [stats, setStats] = useState<AdminOrganizationStat[]>([])
  const [error, setError] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    adminEmail: '',
    adminPhoneNumber: '',
    adminFirstName: '',
    adminLastName: '',
    description: '',
    address: '',
    city: '',
    state: '',
    country: '',
  })

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [pendingRes, statsRes] = await Promise.all([
          adminApi.organizationsPending(),
          adminApi.organizationStats(),
        ])
        const pendingData =
          (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
          (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
          []
        const statsData =
          (statsRes as unknown as { data?: AdminOrganizationStat[] }).data ||
          (statsRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
          []
        setPending(Array.isArray(pendingData) ? pendingData : [])
        setStats(Array.isArray(statsData) ? statsData : [])
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load organizations')
      }
    }
    void load()
  }, [])

  const totals = useMemo(() => {
    const totalOrgs = stats.length
    const pendingCount = pending.length
    const suspendedCount = stats.filter((s) => s.status === 'suspended').length
    const avgCompletion =
      stats.length > 0
        ? Math.round(
            stats.reduce((sum, item) => sum + (item.completionRate ?? 0), 0) / stats.length
          )
        : 0

    return {
      totalOrgs,
      pendingCount,
      suspendedCount,
      avgCompletion,
    }
  }, [stats, pending])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviteError(null)
    setInviteSuccess(null)
    setInviteLoading(true)
    try {
      await adminApi.inviteOrganization({
        ...inviteForm,
        invitationMessage: undefined,
      })
      setInviteSuccess('Invitation sent successfully.')
      setInviteForm({
        name: '',
        email: '',
        phoneNumber: '',
        adminEmail: '',
        adminPhoneNumber: '',
        adminFirstName: '',
        adminLastName: '',
        description: '',
        address: '',
        city: '',
        state: '',
        country: '',
      })
      setInviteOpen(false)
      // refresh data
      const pendingRes = await adminApi.organizationsPending()
      const pendingData =
        (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
        (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
        []
      setPending(Array.isArray(pendingData) ? pendingData : [])
    } catch (err) {
      setInviteError((err as Error)?.message || 'Failed to invite organization')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleResendInvite = async (organizationId: string) => {
    if (resendingId) return
    
    setResendingId(organizationId)
    setError(null)
    
    try {
      await adminApi.resendOrganizationInvite(organizationId)
      
      // Show success message briefly
      setInviteSuccess('Invitation resent successfully!')
      setTimeout(() => setInviteSuccess(null), 3000)
      
      // Refresh pending list
      const pendingRes = await adminApi.organizationsPending()
      const pendingData =
        (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
        (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
        []
      setPending(Array.isArray(pendingData) ? pendingData : [])
    } catch (err) {
      setError((err as Error)?.message || 'Failed to resend invitation')
    } finally {
      setResendingId(null)
    }
  }

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Organizations'
          infoText='Manage invites, approvals, suspensions, and organization performance.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <StatCard title='Total Orgs' value={(totals.totalOrgs || 0).toLocaleString()} subtitle='All organizations' trend='' />
        <StatCard title='Pending' value={(totals.pendingCount || 0).toLocaleString()} subtitle='Awaiting approval' trend='' />
        <StatCard title='Suspended' value={(totals.suspendedCount || 0).toLocaleString()} subtitle='Suspended' trend='' />
        <StatCard title='Avg Completion' value={`${totals.avgCompletion}%`} subtitle='Completion rates' trend='' />
      </div>

      <div className='grid grid-rows-1 xl:grid-rows-2 gap-4'>
          <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3'>
          <h3 className='text-lg font-semibold text-gray-900'>Stats by Organization</h3>
          <div className='grid grid-cols-3 gap-3 text-sm text-gray-800'>
            {stats.length === 0 && (
              <p className='text-sm text-gray-600 col-span-3'>No organization stats available.</p>
            )}
            {stats.map((item) => {
              const name = item.name || 'Organization'
              const completion = item.completionRate ?? 0
              const couples = item.couples ?? 0
              return (
              <div key={item.id} className='p-3 rounded-lg bg-gray-50'>
                <p className='text-xs text-gray-500'>{name}</p>
                <p className='text-lg font-semibold text-gray-900'>Completion {completion}%</p>
                <p className='text-xs text-gray-600'>Couples: {couples}</p>
              </div>
            )})}
          </div>
          <p className='text-xs text-gray-500'>Surface completion rates and totals for comparisons.</p>
        </div>
        <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3'>
          <div className='flex items-center justify-between'>
            <h3 className='text-lg font-semibold text-gray-900'>Pending Approvals</h3>
            <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
              <SheetTrigger asChild>
                <button className='px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800'>
                  Invite Organization
                </button>
              </SheetTrigger>
              <SheetContent side='right' className='w-full sm:max-w-lg overflow-y-auto'>
                <SheetHeader>
                  <SheetTitle>Invite Organization</SheetTitle>
                </SheetHeader>
                <form className='mt-6 space-y-3' onSubmit={handleInvite}>
                  {inviteError && (
                    <div className='text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
                      {inviteError}
                    </div>
                  )}
                  {inviteSuccess && (
                    <div className='text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2'>
                      {inviteSuccess}
                    </div>
                  )}
                  {[
                    { label: 'Organization Name', key: 'name', type: 'text' },
                    { label: 'Organization Email', key: 'email', type: 'email' },
                    { label: 'Organization Phone', key: 'phoneNumber', type: 'tel', placeholder: '+2348012345678' },
                    { label: 'Admin Email', key: 'adminEmail', type: 'email' },
                    { label: 'Admin Phone', key: 'adminPhoneNumber', type: 'tel', placeholder: '+2348012345679' },
                    { label: 'Admin First Name', key: 'adminFirstName', type: 'text' },
                    { label: 'Admin Last Name', key: 'adminLastName', type: 'text' },
                  ].map((field) => (
                    <div key={field.key} className='space-y-1'>
                      <label className='text-xs font-semibold text-gray-700 text-start block'>{field.label}</label>
                      {field.type === 'tel' ? (
                        <PhoneNumberInput
                          value={(inviteForm as Record<string, string>)[field.key] || ''}
                          onChange={(value) =>
                            setInviteForm((prev) => ({
                              ...prev,
                              [field.key]: value,
                            }))
                          }
                          placeholder='Phone number'
                          required={['phoneNumber', 'adminPhoneNumber'].includes(field.key)}
                          containerClassName='border-gray-300'
                          inputClassName='py-0'
                        />
                      ) : (
                        <input
                          type={field.type}
                          placeholder={field.placeholder}
                          className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500'
                          required={['name', 'email', 'phoneNumber', 'adminEmail', 'adminPhoneNumber'].includes(field.key)}
                          value={(inviteForm as Record<string, string>)[field.key]}
                          onChange={(e) =>
                            setInviteForm((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                  ))}
                  <div className='flex justify-end gap-2 pt-2'>
                    <Button type='button' variant='outline' onClick={() => setInviteOpen(false)} disabled={inviteLoading}>
                      Cancel
                    </Button>
                    <Button type='submit' className='bg-gray-900 hover:bg-gray-800' disabled={inviteLoading}>
                      {inviteLoading ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                </form>
              </SheetContent>
            </Sheet>
          </div>
          
          {/* Table */}
          <div className='overflow-x-auto'>
            {pending.length === 0 ? (
              <p className='py-3 text-sm text-gray-600 text-center'>No pending organizations.</p>
            ) : (
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-gray-200 text-start'>
                    <th className='text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      Organization Name
                    </th>
                    <th className='text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      ID / Reference
                    </th>
                    <th className='text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      Couples
                    </th>
                    <th className='text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      Completion Rate
                    </th>
                    <th className='text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {pending.map((item) => {
                    const name = item.name || 'Organization'
                    const adminEmail = item.id || ''
                    const status = item.status || 'pending'
                    const couples = item.couples ?? 0
                    const completionRate = item.completionRate ?? 0
                    
                    return (
                      <tr key={item.id} className='hover:bg-gray-50 text-start'>
                        <td className='py-3 px-4'>
                          <p className='font-semibold text-gray-900 text-sm'>{name}</p>
                        </td>
                        <td className='py-3 px-4'>
                          <p className='text-gray-600 text-xs font-mono'>{adminEmail}</p>
                        </td>
                        <td className='py-3 px-4 text-center'>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : status === 'suspended'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </td>
                        <td className='py-3 px-4 text-center'>
                          <p className='text-gray-900 text-sm font-medium'>{couples}</p>
                        </td>
                        <td className='py-3 px-4 text-center'>
                          <div className='flex items-center justify-center gap-2'>
                            <div className='w-16 bg-gray-200 rounded-full h-2'>
                              <div 
                                className='bg-orange-500 h-2 rounded-full' 
                                style={{ width: `${completionRate}%` }}
                              />
                            </div>
                            <span className='text-gray-900 text-sm font-medium'>{completionRate}%</span>
                          </div>
                        </td>
                        <td className='py-3 px-4 text-right'>
                          {status === 'pending' && (
                            <button
                              onClick={() => handleResendInvite(item.id)}
                              disabled={resendingId === item.id}
                              className='px-3 py-1 rounded-md bg-orange-50 text-orange-700 hover:bg-orange-100 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                              {resendingId === item.id ? 'Resending...' : 'Resend Invite'}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

      
      </div>
      {error && <p className='text-sm text-red-600'>{error}</p>}
    </div>
  )
}
